/**
 * Occurrence facts and tallies (R12) — what just HAPPENED, as data.
 *
 * @remarks
 * An occurrence is a per-input event the trigger machinery can listen for:
 * a command executed, a command rejected, a parse failed, a D27 reaction
 * spoken. Each one bumps two engine-maintained tallies — the family
 * (`parse_failed`) and the narrowed key (`parse_failed:gibberish_input`) —
 * whether or not anything reads them, so "respond to repeated behavior"
 * content can arrive later without a schema change. The
 * `occurrence_tally_at_least` condition is the read side.
 *
 * Tallies live in `narrative.occurrenceTallies` (committed state, D23), so
 * replays count byte-identically and saves remember how often the player
 * fought the parser.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from '../conditions/command-in-hand.js';

import { appendTranscript, inputNamespaceCursor } from '../execution/transcript-cursor.js';
import { evaluateTriggers } from './evaluate-triggers.js';

/** The occurrence families a trigger can listen for — the R12 §7.3 set, and (D235) the answer to an actor's question. */
export type OccurrenceFamily = 'command_executed' | 'command_rejected' | 'dialogue_answered' | 'parse_failed' | 'reaction_spoken';

/**
 * The families an EXECUTED turn raises — the turns that move time and may
 * move the world, which a state watcher (a trigger with no `on`) listens
 * at. A command the player typed (`command_executed`) and, since D235, an
 * answer the player gave to an actor's question (`dialogue_answered`): the
 * answer is not a command, but it is a turn, and the watchers must see
 * what it made true.
 */
const EXECUTED_TURN_FAMILIES: readonly OccurrenceFamily[] = ['command_executed', 'dialogue_answered'];

/** Did an executed turn raise this occurrence — is this a site where state watchers listen? */
export const isExecutedTurnFamily = (family: OccurrenceFamily): boolean => EXECUTED_TURN_FAMILIES.includes(family);

/** One occurrence: the family, and the narrowing detail when the family has one. */
export interface OccurrenceFact {
	/**
	 * D226 (E19): the command this occurrence is about, when it is one a
	 * command raised (`command_executed`, `command_rejected`) — the triggers
	 * listening for it may ask what the command named (`direct_object_is`,
	 * `indirect_object_is`). A parse failure or a reaction has no command,
	 * and neither has an answer to a prompt — the player's words were an
	 * answer, not a sentence about things.
	 */
	readonly commandInHand?: CommandInHand;
	/** The narrowed discriminant — an action type, rejection reason, failure kind, reaction key, or (D235) the prompt answered. */
	readonly detail?: string;
	readonly family: OccurrenceFamily;
	/**
	 * D235: WHICH answer was given, when the occurrence is a `dialogue_answered`
	 * — beside `detail`, which names the prompt. The tally keys on the prompt
	 * alone (`dialogue_answered:<promptId>` — "how often has she asked and
	 * been answered"); a trigger narrows to the option through its matcher.
	 */
	readonly optionId?: string;
}

/**
 * The state with this occurrence tallied — family key always, narrowed key
 * when a detail exists.
 *
 * @param state - The state BEFORE the occurrence
 * @param occurrence - What happened
 * @returns The state with both tallies advanced
 */
export const tallyOccurrence = (state: RuntimeState, occurrence: OccurrenceFact): RuntimeState => {
	const tallies = { ...state.narrative.occurrenceTallies };

	tallies[occurrence.family] = (tallies[occurrence.family] ?? 0) + 1;

	if (occurrence.detail !== undefined) {
		const narrowedKey = `${occurrence.family}:${occurrence.detail}`;

		tallies[narrowedKey] = (tallies[narrowedKey] ?? 0) + 1;
	}

	return { ...state, narrative: { ...state.narrative, occurrenceTallies: tallies }};
};

/** The result of one input-level occurrence: entries spoken (committed), and the state after. */
export interface InputOccurrenceResponse {
	readonly entries: readonly RuntimeTranscriptEntry[];
	readonly state: RuntimeState;
}

/**
 * Tally one NON-EXECUTED occurrence and let its listeners speak (R12).
 *
 * @remarks
 * The evaluation site for the occurrences that never reach `executeTurn`: a
 * failed parse, a rejected command, a spoken reaction. The occurrence is
 * tallied FIRST (a trigger gated "on the third failure" fires on the third),
 * matching occurrence triggers fire through the ordinary sweep, and their
 * entries are committed under the input's id namespace — no turn was
 * consumed, so no turn-namespace entry may exist. Validation guarantees
 * these triggers only SPEAK (`append_narrative` / `speak_voice_line`): a
 * refused input never moves the world.
 *
 * @param bundle - The loaded bundle
 * @param state - The committed state so far (entries already appended)
 * @param inputOrdinal - This input's ordinal (id namespace)
 * @param entrySequence - How many entries this input has already stamped
 * @param occurrence - What just happened
 * @returns The spoken entries and the state with them committed
 */
export const respondToInputOccurrence = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	inputOrdinal: number,
	entrySequence: number,
	occurrence: OccurrenceFact,
): InputOccurrenceResponse => {
	const talliedState = tallyOccurrence(state, occurrence);
	const triggered = evaluateTriggers(
		bundle,
		talliedState,
		inputNamespaceCursor(inputOrdinal, entrySequence, talliedState),
		occurrence,
	);

	return { entries: triggered.entries, state: appendTranscript(triggered.state, triggered.entries) };
};
